import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ApiError } from '../../../../api/client';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import { TableScrollWrapper } from '../../../../components/TableScrollWrapper';
import { useConnections } from '../../../schema/hooks/useConnections';
import { useToastStore } from '../../../../stores/toastStore';
import type { InstantiateSectorTemplateBody, SectorTemplateDetailResponse } from '../api';
import { useInstantiateSectorTemplate, useSectorTemplate } from '../hooks';

/**
 * Single-template preview + instantiation form. The form fields cover everything
 * `InstantiateSectorTemplateUseCase` accepts: model provider, optional name/slug overrides.
 */
export function SectorTemplatePreviewPage(): JSX.Element {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const pushToast = useToastStore((s) => s.push);
  const { data: template, isLoading, isError, error } = useSectorTemplate(templateId);
  const { data: providers, isLoading: providersLoading } = useConnections();
  const mutation = useInstantiateSectorTemplate();

  const [modelProviderId, setModelProviderId] = useState<string>('');
  const [overrideName, setOverrideName] = useState<string>('');
  const [overrideSlug, setOverrideSlug] = useState<string>('');

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (isError || template === undefined) {
    return <ErrorBanner error={error} />;
  }

  const submit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (templateId === undefined) {
      return;
    }
    if (modelProviderId === '') {
      pushToast({ tone: 'error', title: 'Selecciona un proveedor IA antes de instanciar.' });
      return;
    }
    const body: InstantiateSectorTemplateBody = { modelProviderId };
    if (overrideName !== '') {
      body.overrideName = overrideName;
    }
    if (overrideSlug !== '') {
      body.overrideSlug = overrideSlug;
    }
    mutation.mutate(
      { templateId, body },
      {
        onSuccess: (created) => {
          pushToast({ tone: 'success', title: `Borrador "${created.slug}" creado desde plantilla.` });
          navigate(`/document-types/${encodeURIComponent(created.slug)}`);
        },
        onError: (err) => {
          if (err instanceof ApiError && err.status === 422) {
            pushToast({
              tone: 'error',
              title: 'Slug no disponible',
              description: err.message !== '' ? err.message : 'Ya hay un tipo con ese slug. Usa el override de slug.',
            });
            return;
          }
          const description = err instanceof Error ? err.message : 'No se pudo instanciar la plantilla.';
          pushToast({ tone: 'error', title: 'Error al instanciar', description });
        },
      },
    );
  };

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">{template.name}</h1>
          <p className="mt-1 text-sm text-slate-500">
            {template.sector} · {template.locale} · {template.fields.length} campos
          </p>
        </div>
        <Link to="/document-types/templates" className="text-sm text-slate-500 hover:underline">
          ← Volver al catálogo
        </Link>
      </header>

      <p className="text-sm text-slate-600">{template.description}</p>

      <section aria-labelledby="fields-heading" className="card overflow-hidden">
        <h2 id="fields-heading" className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-700">
          Campos
        </h2>
        <TableScrollWrapper>
        <table className="w-full min-w-[520px] text-left text-sm">
          <thead className="text-xs font-medium uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2">Nombre</th>
              <th className="px-4 py-2">Tipo</th>
              <th className="px-4 py-2">Requerido</th>
              <th className="px-4 py-2">Crítico</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {template.fields.map((field) => (
              <tr key={field.name}>
                <td className="px-4 py-2 font-mono text-xs text-slate-700">{field.name}</td>
                <td className="px-4 py-2 text-slate-600">{field.type}</td>
                <td className="px-4 py-2 text-slate-600">{field.required ? 'Sí' : 'No'}</td>
                <td className="px-4 py-2 text-slate-600">{field.critical ? 'Sí' : 'No'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </TableScrollWrapper>
      </section>

      <ThresholdsCard template={template} />

      <form onSubmit={submit} className="card space-y-4 p-4">
        <h2 className="text-base font-semibold text-slate-900">Instanciar en mi tenant</h2>

        <label className="block text-sm">
          <span className="font-medium text-slate-700">Proveedor IA *</span>
          <select
            value={modelProviderId}
            onChange={(e) => setModelProviderId(e.target.value)}
            required
            disabled={providersLoading}
            className="mt-1 block w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-primary focus:outline-none"
          >
            <option value="">Selecciona…</option>
            {(providers ?? []).map((p) => (
              <option key={p.providerId} value={p.providerId}>
                {p.alias}
              </option>
            ))}
          </select>
        </label>

        <label className="block text-sm">
          <span className="font-medium text-slate-700">Nombre (opcional)</span>
          <input
            type="text"
            value={overrideName}
            onChange={(e) => setOverrideName(e.target.value)}
            placeholder={template.name}
            className="mt-1 block w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-primary focus:outline-none"
          />
        </label>

        <label className="block text-sm">
          <span className="font-medium text-slate-700">Slug (opcional)</span>
          <input
            type="text"
            value={overrideSlug}
            onChange={(e) => setOverrideSlug(e.target.value)}
            placeholder={template.id}
            pattern="^[a-z0-9-]+$"
            className="mt-1 block w-full rounded border border-slate-300 px-3 py-2 font-mono text-sm focus:border-primary focus:outline-none"
          />
          <span className="mt-1 block text-xs text-slate-500">
            Si lo dejas vacío se usa el id de la plantilla. Letras minúsculas, dígitos y guiones.
          </span>
        </label>

        <div className="flex items-center justify-end gap-3">
          <Link to="/document-types/templates" className="text-sm text-slate-500 hover:underline">
            Cancelar
          </Link>
          <button type="submit" className="btn-primary" disabled={mutation.isPending}>
            {mutation.isPending ? 'Instanciando…' : 'Instanciar en mi tenant'}
          </button>
        </div>
      </form>
    </div>
  );
}

function ThresholdsCard({ template }: { template: SectorTemplateDetailResponse }): JSX.Element {
  const t = template.thresholds;
  return (
    <section className="card grid grid-cols-2 gap-3 p-4 text-sm md:grid-cols-4">
      <Stat label="Aviso campo" value={`${(t.fieldWarning * 100).toFixed(0)}%`} />
      <Stat label="Crítico campo" value={`${(t.fieldCritical * 100).toFixed(0)}%`} />
      <Stat label="Ratio revisión doc" value={`${(t.documentReviewRatio * 100).toFixed(0)}%`} />
      <Stat label="Rechazo global" value={`${(t.documentRejectGlobalScore * 100).toFixed(0)}%`} />
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-slate-400">{label}</p>
      <p className="mt-0.5 text-base font-semibold text-slate-900">{value}</p>
    </div>
  );
}

function ErrorBanner({ error }: { error: unknown }): JSX.Element {
  const message = error instanceof ApiError && error.status === 404
    ? 'Plantilla no encontrada.'
    : error instanceof ApiError
    ? error.message
    : 'No se pudo cargar la plantilla.';
  return (
    <div role="alert" className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
      {message}
    </div>
  );
}
